import React from 'react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import { HelpCircle } from 'lucide-react';

/**
 * "Explain this change" popover for plan_change audit events.
 * Reads the `prorate_meta` payload written by the admin.py plan-upgrade
 * handler and renders the full proration math so admins can audit expiry
 * date movement without a manual recalculation.
 *
 * If `meta` is missing (e.g. non-prorated switches, or events logged
 * before this field existed) the button is not rendered at all.
 */
const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return iso; }
};

const Row = ({ label, value, mono = false }) => (
  <div className="flex justify-between gap-3 py-0.5">
    <span className="text-slate-500">{label}</span>
    <span className={`text-slate-900 text-right ${mono ? 'font-mono' : ''}`}>{value}</span>
  </div>
);

const ExplainProrateButton = ({ meta, variant = 'inline' }) => {
  if (!meta || meta.new_days == null) return null;

  const {
    src_plan_name, src_price, src_days, src_per_day,
    tgt_plan_name, tgt_price, tgt_days, tgt_per_day,
    src_remaining_days, src_remaining_value, new_days, new_expires_at,
  } = meta;

  return (
    <Popover>
      <PopoverTrigger
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-800 hover:underline rounded"
        data-testid="explain-prorate-trigger"
      >
        <HelpCircle className="w-3 h-3" />
        Explain this change
      </PopoverTrigger>
      <PopoverContent
        align={variant === 'inline' ? 'start' : 'center'}
        className="w-80 text-xs"
        onClick={(e) => e.stopPropagation()}
        data-testid="explain-prorate-content"
      >
        <div className="font-medium text-sm mb-2 text-slate-900">
          Plan change · Prorated
        </div>

        <div className="space-y-2">
          <div className="rounded-md bg-slate-50 border border-slate-200 p-2">
            <div className="font-medium text-slate-700 mb-1">Source</div>
            <Row label="Plan" value={src_plan_name || '—'} />
            <Row label="Price" value={`₹${src_price} / ${src_days}d`} mono />
            <Row label="Per day" value={`₹${Number(src_per_day).toFixed(2)}`} mono />
            <Row label="Days remaining" value={Number(src_remaining_days).toFixed(2)} mono />
            <Row label="Unused value" value={`₹${Number(src_remaining_value).toFixed(2)}`} mono />
          </div>

          <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2">
            <div className="font-medium text-emerald-800 mb-1">Target</div>
            <Row label="Plan" value={tgt_plan_name || '—'} />
            <Row label="Price" value={`₹${tgt_price} / ${tgt_days}d`} mono />
            <Row label="Per day" value={`₹${Number(tgt_per_day).toFixed(2)}`} mono />
          </div>

          <div className="rounded-md bg-blue-50 border border-blue-200 p-2 space-y-1">
            <div className="font-medium text-blue-800">Formula</div>
            <div className="font-mono text-[11px] text-blue-900 break-all">
              ⌈₹{Number(src_remaining_value).toFixed(2)} ÷ ₹{Number(tgt_per_day).toFixed(2)}⌉
              {' = '}<strong>{new_days} day{new_days === 1 ? '' : 's'}</strong>
            </div>
            <div className="text-[11px] text-blue-900">
              New expiry: <strong>{fmtDate(new_expires_at)}</strong>
            </div>
          </div>

          <div className="text-[10px] text-slate-400 italic pt-1 border-t">
            Days rounded up so no fractional day is lost.
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ExplainProrateButton;
